import { toast } from '@/view/components/ui/use-toast'
import { RaffleFormValues } from '@/view/pages/Sorteios/hooks/useRaffleForm'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { raffleService } from '../services/raffleService'

export const useRaffleMutations = () => {
  const queryClient = useQueryClient()

  const createRaffle = useMutation({
    mutationFn: raffleService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raffles'] })
      toast({
        title: 'Sorteio criado com sucesso',
        description: 'O sorteio foi criado com sucesso',
      })
    },
    onError: () => {
      toast({
        title: 'Erro ao criar sorteio',
        description: 'Ocorreu um erro ao criar o sorteio',
        variant: 'destructive',
      })
    },
  })

  const updateRaffle = useMutation({
    mutationFn: ({
      raffleId,
      payload,
    }: {
      raffleId: string
      payload: RaffleFormValues
    }) => raffleService.update(raffleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raffles'] })
      toast({
        title: 'Sorteio atualizado com sucesso',
        description: 'O sorteio foi atualizado com sucesso',
      })
    },
    onError: () => {
      toast({
        title: 'Erro ao atualizar sorteio',
        description: 'Ocorreu um erro ao atualizar o sorteio',
        variant: 'destructive',
      })
    },
  })

  const deleteRaffle = useMutation({
    mutationFn: (raffleId: string) => raffleService.delete(raffleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raffles'] })
      toast({
        title: 'Sorteio excluído com sucesso',
        description: 'O sorteio foi excluído com sucesso',
      })
    },
    onError: () => {
      toast({
        title: 'Erro ao excluir sorteio',
        description: 'Ocorreu um erro ao excluir o sorteio',
        variant: 'destructive',
      })
    },
  })

  const deleteRafflePrize = useMutation({
    mutationFn: ({
      raffleId,
      prizeIndex,
    }: {
      raffleId: string
      prizeIndex: number
    }) => raffleService.deletePrize(raffleId, prizeIndex),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raffles'] })
      toast({
        title: 'Prêmio excluído com sucesso',
        description: 'O prêmio foi excluído com sucesso',
      })
    },
    onError: () => {
      toast({
        title: 'Erro ao excluir prêmio',
        description: 'Ocorreu um erro ao excluir o prêmio',
        variant: 'destructive',
      })
    },
  })

  const drawRaffle = useMutation({
    mutationFn: ({ raffleId, code }: { raffleId: string; code?: string }) =>
      raffleService.draw(raffleId, code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raffles'] })
      toast({
        title: 'Sorteio realizado com sucesso',
        description: 'O sorteio foi realizado com sucesso',
      })
    },
    onError: (error) => {
      const title = 'Erro ao sortear cupons'

      if (error instanceof AxiosError) {
        const description =
          error.response?.status === 409
            ? 'Esse sorteio já foi realizado'
            : 'Ocorreu um erro ao sortear os cupons'

        toast({
          title,
          description,
          variant: 'destructive',
        })

        return
      }

      toast({
        title,
        description: 'Ocorreu um erro ao realizar o sorteio',
        variant: 'destructive',
      })
    },
  })

  return {
    createRaffle,
    updateRaffle,
    deleteRaffle,
    deleteRafflePrize,
    drawRaffle,
  }
}
